import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {Loader} from '@googlemaps/js-api-loader';
import {CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import {isAuthRedirectError, requireAuth} from '../../services/auth.service';
import {logError} from '../../services/logger.service';
import {
  FilterChip,
  LabeledOutlineBox,
  ListRow,
  StatusListState,
  useMapListLink,
  valueText,
} from './ListPageKit';
import {groupPins, PinGroup} from './map-pin-groups';

/*
 * The map of a list page (Appointments, Jobs), ported from Android's
 * AppointmentsMapView.kt / MapPins.kt: one teardrop pin per record, a popup
 * with the name, one line and Open, the satellite and recenter buttons, and
 * the "not on the map" line. Inside a MapListPage it is either the full map
 * (under 900px: its own filter strip, exactly Android) or the map beside the
 * list (the list's chips and search decide the pins; no strip of its own).
 */

/** RedAction: every pin, on both maps. */
const PIN_COLOR = '#f51a1d';
/** MapPinShape from the prototype (viewBox 0 0 26 37), cut at the point so the box ends on it. */
const PIN_PATH = 'M2.8 19.3A12 12 0 1 1 23.2 19.3L13 36Z';
const SVG_NS = 'http://www.w3.org/2000/svg';

/** A mouse resting on a pin opens its popup after this, so sweeping the map does not flash every pin. */
const HOVER_OPEN_MS = 150;
/** ...and it closes this long after the mouse left both the pin and the popup. */
const HOVER_CLOSE_MS = 1_000;
/**
 * A tapped pin's popup gets out of the way on its own (Android's
 * POPUP_TIMEOUT_MS). A shared pin's list stays until it is put away: it
 * takes longer than four seconds to read and pick from.
 */
const TOUCH_POPUP_MS = 4_000;
/** A press that travels further than this is a pan, not a tap on a pin. */
const TAP_SLOP = 6;
/** One pin: close enough to see the street, not the driveway. */
const SINGLE_PIN_ZOOM = 15;
/** Room left around the pins when the map frames them all. */
const FRAME_PADDING = 64;
/** The popup keeps this far from the map's edges. */
const POPUP_MARGIN = 8;
/** The popup's gap above the pin's head (see .lp-pin-popup). */
const POPUP_GAP = 4;

type LatLng = {lat: number; lng: number};

/**
 * Where a record's pin goes, or null when its address never resolved (the
 * geocoder leaves 0,0). Those get no pin rather than an approximate one, and
 * stay reachable through the "not on the map" line.
 */
const pinPosition = (row: ListRow): LatLng | null => {
  const at = row.location;
  if (!at || !Number.isFinite(at.lat) || !Number.isFinite(at.lng)) return null;
  if (at.lat === 0 && at.lng === 0) return null;
  if (Math.abs(at.lat) > 90 || Math.abs(at.lng) > 180) return null;
  return {lat: at.lat, lng: at.lng};
};

/** A pin's key: `row:<record>` for a record alone, `spot:<lat>,<lng>` for several on one spot. */
type PinKey = string;

const isShared = (key: PinKey) => key.startsWith('spot:');

interface PinMarker {
  marker: google.maps.marker.AdvancedMarkerElement;
  pin: HTMLDivElement;
  art: SVGSVGElement;
  /** How many records share the pin; hidden on a pin of one. */
  count: HTMLSpanElement;
  /** Where the open popup is rendered: inside the marker, so it moves with the map. */
  slot: HTMLDivElement;
}

const UpChevron = ({down}: {down?: boolean}) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" style={down ? {transform: 'rotate(180deg)'} : undefined}>
    <path d="M5 15L12 8L19 15" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Android's MenuIcons.Layers and MenuIcons.Crosshair.
const LayersIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2.8L21.2 7.6L12 12.4L2.8 7.6Z"/>
    <path d="M2.8 12L12 16.8L21.2 12"/>
    <path d="M2.8 16.4L12 21.2L21.2 16.4"/>
  </svg>
);

const CrosshairIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4.8 12a7.2 7.2 0 1 0 14.4 0a7.2 7.2 0 1 0-14.4 0" strokeWidth={1.9}/>
    <path d="M12 1.8v3.4M12 18.8v3.4M1.8 12h3.4M18.8 12h3.4" strokeWidth={2.1}/>
    <path d="M10.1 12a1.9 1.9 0 1 0 3.8 0a1.9 1.9 0 1 0-3.8 0" fill="currentColor" stroke="none"/>
  </svg>
);

const RecordsMap = ({list, filterLabel, noun, loading = false, error, onRetry}: {
  /** The page's list state: the map shows these rows (`location`, `mapLine`, `onClick` for Open). */
  list: StatusListState;
  /** The full map's filter strip label ("Appointment Filters"). */
  filterLabel: string;
  /** What a record is called in the map's notes. */
  noun: {one: string; many: string};
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
}) => {
  const navigate = useNavigate();
  const link = useMapListLink();
  const split = link?.split ?? false;
  const setActiveKey = link?.setActiveKey;
  const hoveredKey = link?.hoveredKey ?? null;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [satellite, setSatellite] = useState(false);
  const [filtersVisible, setFiltersVisible] = useState(true);
  const [showOffMap, setShowOffMap] = useState(false);
  const [recenterTick, setRecenterTick] = useState(0);
  const [openKey, setOpenKey] = useState<PinKey | null>(null);
  const [popupPlace, setPopupPlace] = useState<{dx: number; below: boolean} | null>(null);

  // Beside the list, its chips AND its search decide the pins. Alone, the map
  // has its own strip of the same chips and no search, as on Android.
  const rows = split ? list.shown : list.inChip;
  const ownStrip = !split && list.chips.length > 0;
  const plotted = useMemo(() => rows.filter(row => pinPosition(row) != null), [rows]);
  const offMap = useMemo(() => rows.filter(row => pinPosition(row) == null), [rows]);
  // Records on the very same spot share one pin with a count (map-pin-groups.ts).
  const pins = useMemo(() => groupPins(rows, pinPosition, row => row.key), [rows]);
  const openPin: PinGroup<ListRow> | undefined = openKey == null ? undefined : pins.find(pin => pin.key === openKey);
  // A pin of one record is that record (the list's card lights up for it).
  const openRow = openPin?.rows.length === 1 ? openPin.rows[0] : undefined;

  // ---- The map ----------------------------------------------------------------

  useEffect(() => {
    let cancelled = false;
    const loader = new Loader({
      apiKey: "AIzaSyBLw50vYSozTEviDIpu94K1KuwM4X_hDUM",
      version: "beta",
    });
    requireAuth(navigate)
      .then(async () => {
        const {Map} = await loader.importLibrary('maps') as google.maps.MapsLibrary;
        await loader.importLibrary('marker');
        // The chain can resolve after the map closed; never hand Maps a null container.
        if (cancelled || !containerRef.current) return;
        const created = new Map(containerRef.current, {
          // The lower 48 until the pins frame it.
          center: {lat: 39.5, lng: -98.35},
          zoom: 4,
          maxZoom: 22,
          mapTypeId: 'roadmap',
          mapId: '100c664330d4fbca',
          tilt: 0,
          // Pan and zoom, nothing else: the round buttons replace Google's own controls.
          disableDefaultUI: true,
          clickableIcons: false,
          // The map is the page here: the wheel zooms and one finger pans.
          gestureHandling: 'greedy',
        });
        // Framing waits for this: a fit asked for before the map has drawn
        // once is cut off and leaves the default view.
        google.maps.event.addListenerOnce(created, 'idle', () => {
          if (!cancelled) setMapLoaded(true);
        });
        setMap(created);
      })
      .catch(e => {
        if (cancelled || isAuthRedirectError(e)) return;
        logError('Error loading the records map', {}, e);
        setFailed(true);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Hybrid, not plain satellite: aerial with the street names still on it.
    map?.setMapTypeId(satellite ? 'hybrid' : 'roadmap');
  }, [map, satellite]);

  // ---- The popup: hover with a mouse, Android's tap on touch ---------------------

  const openTimer = useRef<number>();
  const closeTimer = useRef<number>();
  const openKeyRef = useRef<PinKey | null>(null);
  openKeyRef.current = openKey;

  const closePopup = useCallback(() => {
    window.clearTimeout(openTimer.current);
    window.clearTimeout(closeTimer.current);
    setOpenKey(null);
  }, []);

  const closeAfter = (ms: number) => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpenKey(null), ms);
  };

  // What the pins' listeners call. A ref, since a marker outlives many renders.
  const pinEvents = useRef({
    enter: (_key: PinKey) => {},
    leave: (_key: PinKey) => {},
    press: (_key: PinKey, _mouse: boolean) => {},
  });
  pinEvents.current = {
    // The mouse came onto a pin, or onto its popup (which is inside the pin's marker).
    enter: key => {
      window.clearTimeout(closeTimer.current);
      window.clearTimeout(openTimer.current);
      if (key === openKeyRef.current) return;
      openTimer.current = window.setTimeout(() => setOpenKey(key), HOVER_OPEN_MS);
    },
    leave: key => {
      window.clearTimeout(openTimer.current);
      if (key === openKeyRef.current) closeAfter(HOVER_CLOSE_MS);
    },
    // A click opens at once and closes like a hover; a tap shows for four
    // seconds, and tapping the open pin again starts the four seconds over.
    press: (key, mouse) => {
      window.clearTimeout(openTimer.current);
      window.clearTimeout(closeTimer.current);
      setOpenKey(key);
      if (!mouse && !isShared(key)) closeAfter(TOUCH_POPUP_MS);
    },
  };

  useEffect(() => () => {
    window.clearTimeout(openTimer.current);
    window.clearTimeout(closeTimer.current);
  }, []);

  // A click on the map itself puts the popup away.
  useEffect(() => {
    if (!map) return;
    const listener = map.addListener('click', closePopup);
    return () => listener.remove();
  }, [map, closePopup]);

  // A pin that left the filter (or the data) takes its popup with it.
  useEffect(() => {
    if (openKey != null && !openPin) closePopup();
  }, [openKey, openPin, closePopup]);

  // Beside the list, the open pin's card lights up and the list scrolls to it.
  useEffect(() => {
    setActiveKey?.(openRow ? openRow.key : null);
  }, [openRow, setActiveKey]);

  // ---- The pins ------------------------------------------------------------------

  const markers = useRef(new Map<PinKey, PinMarker>());

  useEffect(() => {
    if (!map) return;
    const current = markers.current;
    const wanted = new Set<PinKey>();
    pins.forEach(group => {
      const position = group.position;
      wanted.add(group.key);
      let entry = current.get(group.key);
      if (!entry) {
        entry = createPin(group.key, pinEvents);
        entry.marker.map = map;
        current.set(group.key, entry);
      }
      const at = entry.marker.position;
      if (!at || at.lat !== position.lat || at.lng !== position.lng) entry.marker.position = position;
      const many = group.rows.length;
      entry.pin.setAttribute('aria-label', many === 1 ? group.rows[0].title : `${many} ${noun.many} at this spot`);
      entry.count.textContent = many > 1 ? String(many) : '';
      entry.count.hidden = many < 2;
    });
    current.forEach((entry, key) => {
      if (wanted.has(key)) return;
      // AdvancedMarkerElement must be detached via map=null; Element.remove() can leave ghosts.
      entry.marker.map = null;
      current.delete(key);
    });
  }, [map, pins, noun.many]);

  useEffect(() => () => {
    markers.current.forEach(entry => { entry.marker.map = null; });
    markers.current.clear();
  }, []);

  // The selected pin grows and comes to the front; the pin of the card under the mouse stands out.
  useEffect(() => {
    markers.current.forEach((entry, key) => {
      const selected = key === openKey;
      const hovered = hoveredKey != null && !!pins.find(pin => pin.key === key)?.rows.some(row => row.key === hoveredKey);
      entry.pin.classList.toggle('lp-pin--selected', selected);
      entry.pin.classList.toggle('lp-pin--card-hover', !selected && hovered);
      entry.marker.zIndex = selected ? 2 : hovered ? 1 : 0;
    });
  }, [openKey, hoveredKey, pins, map]);

  // ---- Framing ---------------------------------------------------------------------

  // Every pin in view: when the map opens, when the filter or the search
  // changes what is on it, and on recenter. Never otherwise: panning within a
  // filter is not yanked back.
  const frameKey = plotted.map(row => {
    const at = pinPosition(row)!;
    return `${row.key}:${at.lat},${at.lng}`;
  }).join('|');
  useEffect(() => {
    if (!map || !mapLoaded) return;
    const points = plotted.map(row => pinPosition(row)!);
    if (points.length === 0) return; // Nothing to frame; leave the map where it is.
    if (points.length === 1) {
      map.setCenter(points[0]);
      map.setZoom(SINGLE_PIN_ZOOM);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    points.forEach(point => bounds.extend(point));
    map.fitBounds(bounds, FRAME_PADDING);
    // Pins sharing one address would otherwise frame down to the rooftop.
    google.maps.event.addListenerOnce(map, 'idle', () => {
      if ((map.getZoom() ?? 0) > SINGLE_PIN_ZOOM) map.setZoom(SINGLE_PIN_ZOOM);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, mapLoaded, frameKey, recenterTick]);

  // ---- Placing the popup ----------------------------------------------------------

  const popupRef = useRef<HTMLDivElement | null>(null);

  // Centred above the pin; below it when there is no room above, and slid
  // sideways to stay on the map.
  const placePopup = useCallback(() => {
    const popup = popupRef.current;
    const container = containerRef.current;
    const entry = openKey == null ? undefined : markers.current.get(openKey);
    if (!popup || !container || !entry) return;
    const pin = entry.art.getBoundingClientRect();
    const box = container.getBoundingClientRect();
    const width = popup.offsetWidth;
    const centred = pin.left + pin.width / 2 - width / 2;
    const left = Math.min(Math.max(centred, box.left + POPUP_MARGIN), Math.max(box.left + POPUP_MARGIN, box.right - width - POPUP_MARGIN));
    const next = {
      dx: Math.round(left - centred),
      below: pin.top - POPUP_GAP - popup.offsetHeight < box.top + POPUP_MARGIN,
    };
    setPopupPlace(previous => previous && previous.dx === next.dx && previous.below === next.below ? previous : next);
  }, [openKey]);

  useLayoutEffect(() => {
    setPopupPlace(null);
    placePopup();
  }, [placePopup, openPin]);

  useEffect(() => {
    if (!map || openKey == null) return;
    const listener = map.addListener('bounds_changed', placePopup);
    return () => listener.remove();
  }, [map, openKey, placePopup]);

  const openSlot = openPin ? markers.current.get(openPin.key)?.slot : undefined;

  const openRecord = (row: ListRow) => {
    closePopup();
    setShowOffMap(false);
    row.onClick?.();
  };

  // ---- The view ---------------------------------------------------------------------

  return (
    <div className="lp-mapview">
      {ownStrip && filtersVisible && (
        <LabeledOutlineBox label={filterLabel} style={{margin: '8px 12px 0'}}>
          <div className="lp-mapview-chips" role="group" aria-label="Filters">
            {list.chips.map(chip => (
              <FilterChip
                key={chip}
                label={chip}
                active={chip === list.activeChip}
                searching={false}
                activeBold
                onClick={() => list.setActiveChip(chip, true)}
              />
            ))}
            <button type="button" className="lp-hide-filters" onClick={() => setFiltersVisible(false)}>
              Hide filters<UpChevron/>
            </button>
          </div>
        </LabeledOutlineBox>
      )}

      {/* Nothing is silently missing: the records without a pin stay reachable through this line. */}
      {offMap.length > 0 && (
        <button type="button" className="lp-map-note" onClick={() => setShowOffMap(true)}>
          {offMap.length === 1 ? `1 ${noun.one} isn't on the map` : `${offMap.length} ${noun.many} aren't on the map`}
        </button>
      )}
      {ownStrip && !loading && !error && rows.length === 0 && (
        <div className="lp-map-note">No {list.activeChip?.toLowerCase()} {noun.many}.</div>
      )}
      {failed && <div className="lp-map-note">The map could not be loaded.</div>}

      <div className="lp-mapview-map">
        <div ref={containerRef} className="lp-map"/>
        {ownStrip && !filtersVisible && (
          <button type="button" className="lp-show-filters" onClick={() => setFiltersVisible(true)}>
            Filters<UpChevron down/>
          </button>
        )}
        <div className="lp-map-controls">
          <button
            type="button"
            className={satellite ? 'lp-map-button lp-map-button--on' : 'lp-map-button'}
            aria-pressed={satellite}
            aria-label={satellite ? 'Show the plain map' : 'Show satellite imagery'}
            onClick={() => setSatellite(on => !on)}
          >
            <LayersIcon/>
          </button>
          <button
            type="button"
            className="lp-map-button"
            aria-label="Recenter the map"
            onClick={() => {
              closePopup();
              setRecenterTick(tick => tick + 1);
            }}
          >
            <CrosshairIcon/>
          </button>
        </div>
        {loading && <div className="lp-map-loading"><CircularProgress/></div>}
        {/* Beside the list, the list says it. */}
        {error && !split && (
          <div className="lp-map-cover">
            <div className="lp-error">{error}</div>
            {onRetry && <button type="button" className="lp-retry" style={{marginLeft: 0}} onClick={onRetry}>Retry</button>}
          </div>
        )}
      </div>

      {openPin && openSlot && createPortal(
        <div
          ref={popupRef}
          className={[
            'lp-pin-popup',
            popupPlace?.below ? 'lp-pin-popup--below' : '',
            openRow ? '' : 'lp-pin-popup--list',
          ].filter(Boolean).join(' ')}
          style={{
            transform: `translateX(calc(-50% + ${popupPlace?.dx ?? 0}px))`,
            // Hidden for the one frame before it has been measured, so it never flashes out of place.
            visibility: popupPlace ? 'visible' : 'hidden',
          }}
        >
          {openRow ? (
            <>
              <div className="lp-pin-popup-name">{openRow.title}</div>
              {openRow.mapLine && <div className="lp-pin-popup-line">{openRow.mapLine}</div>}
              <button type="button" className="lp-pin-popup-open" onClick={() => openRecord(openRow)}>Open</button>
            </>
          ) : (
            // Several records on this one spot: one line each, each with its own Open.
            openPin.rows.map(row => (
              <div key={row.key} className="lp-pin-popup-item">
                <div className="lp-pin-popup-text">
                  <div className="lp-pin-popup-name">{row.title}</div>
                  {row.mapLine && <div className="lp-pin-popup-line">{row.mapLine}</div>}
                </div>
                <button type="button" className="lp-pin-popup-open" onClick={() => openRecord(row)}>Open</button>
              </div>
            ))
          )}
        </div>,
        openSlot,
      )}

      <Dialog open={showOffMap && offMap.length > 0} onClose={() => setShowOffMap(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Not on the map</DialogTitle>
        <DialogContent className="lp">
          <div className="lp-offmap-intro">
            These {noun.many}' addresses couldn't be placed exactly, so they have no pin. Open one from here.
          </div>
          <div className="lp-offmap-list">
            {offMap.map(row => {
              const address = valueText(row.values.address);
              return (
                <button key={row.key} type="button" className="lp-offmap-row" onClick={() => openRecord(row)}>
                  <div className="lp-offmap-name">{row.title}</div>
                  {address && <div>{address}</div>}
                  {row.mapLine && <div>{row.mapLine}</div>}
                </button>
              );
            })}
          </div>
        </DialogContent>
        <DialogActions className="lp">
          <button type="button" className="lp-retry" style={{marginLeft: 0}} onClick={() => setShowOffMap(false)}>Close</button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

/**
 * One pin: the teardrop, a slot for its popup, and the listeners that tell
 * the map what the pointer did to it. A press only counts when it started on
 * the pin itself (not on the popup's Open) and did not travel: Maps hands the
 * pointer-up of a pan that happens to end over a pin to that pin too.
 */
function createPin(
  key: PinKey,
  events: React.MutableRefObject<{
    enter: (key: PinKey) => void;
    leave: (key: PinKey) => void;
    press: (key: PinKey, mouse: boolean) => void;
  }>,
): PinMarker {
  const pin = document.createElement('div');
  pin.className = 'lp lp-pin';
  pin.setAttribute('role', 'button');
  pin.tabIndex = 0;

  const art = document.createElementNS(SVG_NS, 'svg');
  art.setAttribute('viewBox', '0 0 26 36');
  art.setAttribute('class', 'lp-pin-art');
  art.setAttribute('aria-hidden', 'true');
  const shape = document.createElementNS(SVG_NS, 'path');
  shape.setAttribute('d', PIN_PATH);
  shape.setAttribute('fill', PIN_COLOR);
  art.appendChild(shape);

  // The count on a shared pin's head ("2"); empty and hidden on a pin of one.
  const count = document.createElement('span');
  count.className = 'lp-pin-count';
  count.setAttribute('aria-hidden', 'true');
  count.hidden = true;

  const slot = document.createElement('div');
  pin.append(art, count, slot);

  pin.addEventListener('pointerenter', event => {
    if (event.pointerType === 'mouse') events.current.enter(key);
  });
  pin.addEventListener('pointerleave', event => {
    if (event.pointerType === 'mouse') events.current.leave(key);
  });
  let down: {x: number; y: number} | null = null;
  pin.addEventListener('pointerdown', event => {
    down = slot.contains(event.target as Node) ? null : {x: event.clientX, y: event.clientY};
  });
  pin.addEventListener('pointerup', event => {
    const from = down;
    down = null;
    if (!from || Math.hypot(event.clientX - from.x, event.clientY - from.y) > TAP_SLOP) return;
    events.current.press(key, event.pointerType === 'mouse');
  });
  pin.addEventListener('keydown', event => {
    if (event.target !== pin || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    events.current.press(key, true);
  });
  pin.addEventListener('focusout', event => {
    if (!pin.contains(event.relatedTarget as Node | null)) events.current.leave(key);
  });

  const marker = new google.maps.marker.AdvancedMarkerElement({content: pin, gmpClickable: true});
  return {marker, pin, art, count, slot};
}

export default RecordsMap;
